import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getChecklistTemplateById,
    createChecklistTemplate,
    updateChecklistTemplate,
    deleteChecklistTemplate,
    exportChecklistTemplate,
} from './checklistApi';
import { UserContext } from '../../context/UserContext';
import { permissions } from '../../utils/permissions-utils';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import PageTitle from '../../components/PageTitle';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import { cancelButton, confirmButton, pageActionsContainer, deleteButtonInTitleMenu } from '../../components/Buttons';
import { Trash2, Plus, X, ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';
import checklistIcon from '../../assets/protocols/protocol.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import departmentIcon from '../../assets/protocols/department.svg';
import './AddEditChecklistTemplate.scss';
import CommonLayout from '../../components/CommonLayout';

const AddEditChecklistTemplate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hospitals, isInitialized } = useContext(UserContext);
    const nameInputRef = useRef(null);
    const hospitalGroupRef = useRef(null);
    const departmentGroupRef = useRef(null);

    const [template, setTemplate] = useState({
        name: '',
        hospital: '',
        department: '',
        groups: [],
        isCloneable: true,
    });
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [availableDepartments, setAvailableDepartments] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteGroupConfirmOpen, setDeleteGroupConfirmOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedOptionalFields, setExpandedOptionalFields] = useState({});

    const isEditMode = !!id;

    useEffect(() => {
        if (!isInitialized) {
            setIsLoadingInitialData(true);
            return;
        }

        const uniqueHospitals = user ? [...new Set(user.roles.map(role => role.hospital))] : [];
        const uniqueDepartments = user ? [...new Set(user.roles.map(role => role.department))] : [];

        const shouldShowHospitalDropdown = (user?.isSuperAdmin && hospitals.length > 1) || (!user?.isSuperAdmin && uniqueHospitals.length > 1);
        setShowHospitalDropdown(shouldShowHospitalDropdown);

        const shouldShowDepartmentDropdown = (user?.isSuperAdmin && hospitals.length > 0) || (!user?.isSuperAdmin && uniqueDepartments.length > 1);
        setShowDepartmentDropdown(shouldShowDepartmentDropdown);

        if (id) {
            setIsLoadingInitialData(true);
            setError(null);
            getChecklistTemplateById(id)
                .then(data => {
                    setTemplate({
                        name: data.name || '',
                        hospital: data.hospital?._id || data.hospital || '',
                        department: data.department?._id || data.department || '',
                        groups: data.groups || [],
                        isCloneable: data.isCloneable !== undefined ? data.isCloneable : true,
                    });
                    // Expand only first group if multiple groups exist, otherwise expand all
                    const expanded = {};
                    if ((data.groups || []).length === 1) {
                        expanded[0] = true;
                    } else if ((data.groups || []).length > 1) {
                        // Start with all collapsed when multiple groups
                        expanded[0] = false;
                    }
                    setExpandedGroups(expanded);

                    // Auto-expand optional fields for items that already have them
                    const expandedOptFields = {};
                    (data.groups || []).forEach((group, groupIdx) => {
                        group.items?.forEach((item, itemIdx) => {
                            if (item.optionalFields && item.optionalFields.length > 0) {
                                const key = `${groupIdx}-${itemIdx}`;
                                expandedOptFields[key] = true;
                            }
                        });
                    });
                    setExpandedOptionalFields(expandedOptFields);
                })
                .catch(err => {
                    setError('אירעה שגיאה בטעינת התבנית');
                    console.error(err);
                })
                .finally(() => setIsLoadingInitialData(false));
        } else {
            setIsLoadingInitialData(false);
            if (user && !shouldShowHospitalDropdown && uniqueHospitals.length === 1) {
                setTemplate(prev => ({ ...prev, hospital: uniqueHospitals[0] }));
            }
            if (user && !shouldShowDepartmentDropdown && uniqueDepartments.length === 1) {
                setTemplate(prev => ({ ...prev, department: uniqueDepartments[0] }));
            }
        }
    }, [user, id, isInitialized, hospitals]);

    useEffect(() => {
        if (template.hospital) {
            const hospital = hospitals.find(h => h._id === template.hospital);
            if (user?.isSuperAdmin) {
                setAvailableDepartments(hospital?.departments || []);
                return;
            }

            const userDepartmentsForHospital = user?.roles
                ?.filter(role => role.hospital === template.hospital)
                .map(role => role.department) || [];

            const uniqueDepartments = [...new Set(userDepartmentsForHospital)];
            const departments = hospital?.departments.filter(d => uniqueDepartments.includes(d._id)) || [];
            setAvailableDepartments(departments);
        } else {
            setAvailableDepartments([]);
        }
    }, [template.hospital, hospitals, user]);

    const isValidUrl = (url) => {
        if (!url || url.trim() === '') return false;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const validate = () => {
        const newErrors = {};
        let firstErrorRef = null;

        if (!template.name.trim()) {
            newErrors.name = 'שדה חובה';
            if (!firstErrorRef) firstErrorRef = nameInputRef;
        }
        if (showHospitalDropdown && !template.hospital) {
            newErrors.hospital = 'שדה חובה';
            if (!firstErrorRef) firstErrorRef = hospitalGroupRef;
        }
        if (showDepartmentDropdown && !template.department) {
            newErrors.department = 'שדה חובה';
            if (!firstErrorRef) firstErrorRef = departmentGroupRef;
        }

        // Validate URLs in optional fields
        template.groups.forEach((group, groupIndex) => {
            group.items.forEach((item, itemIndex) => {
                item.optionalFields?.forEach((field, fieldIndex) => {
                    if (field.type === 'link') {
                        if (!field.url || field.url.trim() === '') {
                            newErrors[`link-${groupIndex}-${itemIndex}-${fieldIndex}`] = 'קישור חסר';
                        } else if (!isValidUrl(field.url)) {
                            newErrors[`link-${groupIndex}-${itemIndex}-${fieldIndex}`] = 'קישור לא חוקי';
                        }
                    }
                });
            });
        });

        setErrors(newErrors);

        // Scroll to the first error
        if (firstErrorRef?.current) {
            firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTemplate((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleHospitalChange = (e) => {
        const newHospitalId = e.target.value;
        setTemplate(prev => ({ ...prev, hospital: newHospitalId, department: '' }));
        setErrors(prev => ({ ...prev, hospital: '', department: '' }));
    };

    const handleGroupChange = (groupIndex, field, value) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex][field] = value;
        setTemplate((prev) => ({ ...prev, groups: newGroups }));
    };

    const handleItemChange = (groupIndex, itemIndex, field, value) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex].items[itemIndex][field] = value;
        setTemplate((prev) => ({ ...prev, groups: newGroups }));
    };

    const handleOptionalFieldChange = (groupIndex, itemIndex, fieldIndex, field, value) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex].items[itemIndex].optionalFields[fieldIndex][field] = value;
        setTemplate((prev) => ({ ...prev, groups: newGroups }));
    };

    const addGroup = () => {
        const newGroupIndex = template.groups.length;
        setTemplate((prev) => ({
            ...prev,
            groups: [...prev.groups, { title: '', items: [] }],
        }));
        setExpandedGroups(prev => ({ ...prev, [newGroupIndex]: true }));
    };

    const handleRemoveGroupClick = (groupIndex) => {
        setGroupToDelete(groupIndex);
        setDeleteGroupConfirmOpen(true);
    };

    const confirmRemoveGroup = () => {
        if (groupToDelete !== null) {
            const newGroups = template.groups.filter((_, idx) => idx !== groupToDelete);
            setTemplate((prev) => ({ ...prev, groups: newGroups }));
            setDeleteGroupConfirmOpen(false);
            setGroupToDelete(null);
        }
    };

    const cancelRemoveGroup = () => {
        setDeleteGroupConfirmOpen(false);
        setGroupToDelete(null);
    };

    const addItem = (groupIndex) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex].items.push({ text: '', optionalFields: [] });
        setTemplate((prev) => ({ ...prev, groups: newGroups }));
    };

    const removeItem = (groupIndex, itemIndex) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex].items.splice(itemIndex, 1);
        setTemplate((prev) => ({ ...prev, groups: newGroups }));
    };

    const addOptionalField = (groupIndex, itemIndex, type) => {
        const newGroups = [...template.groups];
        const field = { type, label: type === 'details' ? 'פרטים' : '' };
        if (type === 'link') {
            field.url = '';
        } else if (type === 'details') {
            field.value = '';
        }
        newGroups[groupIndex].items[itemIndex].optionalFields.push(field);
        setTemplate(prev => ({ ...prev, groups: newGroups }));

        // Auto-expand the optional fields section when adding a field
        const key = `${groupIndex}-${itemIndex}`;
        setExpandedOptionalFields(prev => ({ ...prev, [key]: true }));
    };

    const removeOptionalField = (groupIndex, itemIndex, fieldIndex) => {
        const newGroups = [...template.groups];
        newGroups[groupIndex].items[itemIndex].optionalFields.splice(fieldIndex, 1);
        setTemplate(prev => ({ ...prev, groups: newGroups }));

        // Collapse the optional fields section if all fields are removed
        if (newGroups[groupIndex].items[itemIndex].optionalFields.length === 0) {
            const key = `${groupIndex}-${itemIndex}`;
            setExpandedOptionalFields(prev => ({ ...prev, [key]: false }));
        }
    };

    const toggleGroupExpanded = (groupIndex) => {
        setExpandedGroups(prev => {
            // If the group is already expanded, collapse it
            if (prev[groupIndex]) {
                return { ...prev, [groupIndex]: false };
            }
            // Otherwise, collapse all other groups and expand this one
            const newExpanded = {};
            newExpanded[groupIndex] = true;
            return newExpanded;
        });
    };

    const toggleOptionalFieldsExpanded = (groupIndex, itemIndex) => {
        const key = `${groupIndex}-${itemIndex}`;
        setExpandedOptionalFields(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        setError(null);
        try {
            // Filter out empty items before submitting
            const cleanedTemplate = {
                ...template,
                groups: template.groups.map(group => ({
                    ...group,
                    items: group.items.filter(item => item.text && item.text.trim() !== '')
                }))
            };

            if (id) {
                await updateChecklistTemplate(id, cleanedTemplate);
            } else {
                await createChecklistTemplate(cleanedTemplate);
            }
            navigate('/checklists');
        } catch (err) {
            setError('אירעה שגיאה בשמירת התבנית');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteChecklistTemplate(id);
            setDeleteConfirmOpen(false);
            navigate('/checklists');
        } catch (error) {
            console.error('שגיאה במחיקת התבנית:', error);
            setError('אירעה שגיאה במחיקת התבנית');
        }
    };

    const handleExport = async () => {
        try {
            const exportData = await exportChecklistTemplate(id);
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${template.name}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('שגיאה בייצוא התבנית:', error);
            setError('אירעה שגיאה בייצוא התבנית');
        }
    };

    const canDelete = id && permissions.canEdit(user, { department: template.department });

    if (!isInitialized || isLoadingInitialData) {
        return (
            <div className="checklist-template-editor-container loading-container">
                <Skeleton>
                    <Skeleton.Text />
                    <Skeleton.Block height='50px' />
                    <Skeleton.Block height='50px' />
                    <Skeleton.Block height='200px' />
                </Skeleton>
            </div>
        );
    }

    return (
        <CommonLayout>
            <div className="checklist-template-editor-container">
                <PageTitle title={isEditMode ? 'עריכת רשומה' : 'רשומה חדשה'}>
                    {canDelete && (
                        <button onClick={() => setDeleteConfirmOpen(true)} style={deleteButtonInTitleMenu} title="מחק תבנית">
                            <Trash2 size={16} />
                        </button>
                    )}
                </PageTitle>

                {error && <div className="form-error">{error}</div>}

                <DeleteConfirmationDialog
                    open={deleteConfirmOpen}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={handleDelete}
                    title={`האם אתה בטוח שברצונך למחוק את התבנית ${template.name}?`}
                />

                <DeleteConfirmationDialog
                    open={deleteGroupConfirmOpen}
                    onClose={cancelRemoveGroup}
                    onConfirm={confirmRemoveGroup}
                    title="מחיקת הקבוצה תמחק את כל הפריטים מתחתיה."
                />

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <InputWithIcon
                            icon={checklistIcon}
                            placeholder="שם התבנית"
                            name="name"
                            value={template.name}
                            onChange={handleInputChange}
                            ref={nameInputRef}
                        />
                        {errors.name && <div className="form-field-error">{errors.name}</div>}
                    </div>

                    <div className="form-group" style={{ marginTop: '15px', marginBottom: '20px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '500',
                            direction: 'rtl'
                        }}>
                            <input
                                type="checkbox"
                                checked={template.isCloneable}
                                onChange={(e) => setTemplate(prev => ({ ...prev, isCloneable: e.target.checked }))}
                                style={{
                                    marginLeft: '10px',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <span>יצירת העתק לכל מטופל</span>
                        </label>
                        <div style={{
                            marginTop: '8px',
                            fontSize: '0.875rem',
                            color: '#666',
                            lineHeight: '1.5',
                            textAlign: 'right',
                            direction: 'rtl'
                        }}>
    אם הרשימה הזו נועדה על מנת לרשום את הפעולות שקורות בזמן אמת - יש לסמן את השדה הזה. במידה והרשימה נועדה רק לתזכורת ואין צורך לתייק מה בוצע ומה לא, ניתן להשאיר את השדה לא מסומן.
                        </div>
                    </div>

                    {showHospitalDropdown && (
                        <div className="form-group" ref={hospitalGroupRef}>
                            <DropdownWithIcon
                                icon={hospitalIcon}
                                placeholder="בית חולים"
                                value={template.hospital}
                                onChange={handleHospitalChange}
                            >
                                {hospitals.map(hospital => (
                                    <option key={hospital._id} value={hospital._id}>
                                        {hospital.nameHebrew || hospital.nameEnglish}
                                    </option>
                                ))}
                            </DropdownWithIcon>
                            {errors.hospital && <div className="form-field-error">{errors.hospital}</div>}
                        </div>
                    )}

                    {showDepartmentDropdown && (
                        <div className="form-group" ref={departmentGroupRef}>
                            <DropdownWithIcon
                                icon={departmentIcon}
                                placeholder="מחלקה"
                                name="department"
                                value={template.department}
                                onChange={handleInputChange}
                                disabled={!template.hospital}
                            >
                                {availableDepartments.map(department => (
                                    <option key={department._id} value={department._id}>
                                        {department.nameHebrew || department.nameEnglish}
                                    </option>
                                ))}
                            </DropdownWithIcon>
                            {errors.department && <div className="form-field-error">{errors.department}</div>}
                        </div>
                    )}

                    <div className="groups-section">
                        <div className="section-header">
                            <h3>קבוצות ופריטים</h3>
                            <button type="button" className="add-group-btn" onClick={addGroup}>
                                <Plus size={16} />
                                הוסף קבוצה
                            </button>
                        </div>

                        {template.groups.map((group, groupIndex) => (
                            <div key={groupIndex} className="group-container">
                                <div className="group-header">
                                    <button
                                        type="button"
                                        className="toggle-group-btn"
                                        onClick={() => toggleGroupExpanded(groupIndex)}
                                    >
                                        {expandedGroups[groupIndex] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </button>
                                    <InputWithIcon
                                        style={{backgroundColor: 'white'}}
                                        placeholder={`שם קבוצה ${groupIndex + 1}`}
                                        value={group.title}
                                        onChange={(e) => handleGroupChange(groupIndex, 'title', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="remove-group-btn"
                                        onClick={() => handleRemoveGroupClick(groupIndex)}
                                        title="הסר קבוצה"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                {expandedGroups[groupIndex] && (
                                    <div className="group-content">
                                        {group.items.map((item, itemIndex) => {
                                            const optionalFieldsKey = `${groupIndex}-${itemIndex}`;
                                            const isOptionalFieldsExpanded = expandedOptionalFields[optionalFieldsKey];

                                            return (
                                                <div key={itemIndex} className="item-container">
                                                    <div className="item-header">
                                                        <InputWithIcon
                                                            placeholder={`פריט ${itemIndex + 1}`}
                                                            value={item.text}
                                                            onChange={(e) => handleItemChange(groupIndex, itemIndex, 'text', e.target.value)}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="toggle-optional-fields-btn"
                                                            onClick={() => toggleOptionalFieldsExpanded(groupIndex, itemIndex)}
                                                            title={isOptionalFieldsExpanded ? "הסתר אפשרויות נוספות" : "הצג אפשרויות נוספות"}
                                                        >
                                                            <MoreHorizontal size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="remove-item-btn"
                                                            onClick={() => removeItem(groupIndex, itemIndex)}
                                                            title="הסר פריט"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>

                                                    {isOptionalFieldsExpanded && (
                                                        <div className="optional-fields">
                                                    {item.optionalFields?.map((field, fieldIndex) => {
                                                        const linkErrorKey = `link-${groupIndex}-${itemIndex}-${fieldIndex}`;
                                                        const fieldTypeLabel = field.type === 'link' ? 'קישור' : field.type === 'details' ? 'פרטים' : 'קלט';
                                                        return (
                                                            <div key={fieldIndex} className="optional-field-wrapper">
                                                                <div className="optional-field">
                                                                    <div className="field-type-label">{fieldTypeLabel}</div>
                                                                    {field.type !== 'details' && (
                                                                        <InputWithIcon
                                                                            placeholder={field.type === 'link' ? 'שם האתר' : 'מה המידע הנוסף הנדרש לתעד כחלק מפעולה זו?'}
                                                                            style={{backgroundColor: 'white'}}
                                                                            value={field.label}
                                                                            onChange={(e) => handleOptionalFieldChange(groupIndex, itemIndex, fieldIndex, 'label', e.target.value)}
                                                                        />
                                                                    )}
                                                                    {field.type === 'link' && (
                                                                        <InputWithIcon
                                                                            placeholder="כתובת הקישור"
                                                                            style={{backgroundColor: 'white'}}
                                                                            value={field.url || ''}
                                                                            onChange={(e) => {
                                                                                handleOptionalFieldChange(groupIndex, itemIndex, fieldIndex, 'url', e.target.value);
                                                                                if (errors[linkErrorKey]) {
                                                                                    setErrors(prev => ({ ...prev, [linkErrorKey]: '' }));
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {field.type === 'details' && (
                                                                        <InputWithIcon
                                                                            placeholder="מידע נוסף שעשוי להיות רלוונטי לצוות המטפל"
                                                                            multiline
                                                                            rows={3}
                                                                            style={{backgroundColor: 'white'}}
                                                                            value={field.value || ''}
                                                                            onChange={(e) => handleOptionalFieldChange(groupIndex, itemIndex, fieldIndex, 'value', e.target.value)}
                                                                        />
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="remove-field-btn"
                                                                        onClick={() => removeOptionalField(groupIndex, itemIndex, fieldIndex)}
                                                                        title="הסר שדה"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                </div>
                                                                {errors[linkErrorKey] && <div className="link-field-error">{errors[linkErrorKey]}</div>}
                                                            </div>
                                                        );
                                                    })}                                                
                                                    <div className="optional-fields-description">
                                                        בנוסף לרשומה, באפשרותך להוסיף פרטים נוספים או קישור הקשורים לפריט - כיצד לבצע אותו, הערות נוספות וכו'. בנוסף, ניתן להוסיף שדה קלט בו יהיה ניתן לתעד דברים רלוונטיים הקשורים לביצוע הפריט.
                                                    </div>
                                                    <div className="add-field-buttons">
                                                        <button
                                                            type="button"                                                        
                                                            onClick={() => addOptionalField(groupIndex, itemIndex, 'details')}
                                                            disabled={item.optionalFields?.some(f => f.type === 'details')}
                                                        >
                                                            + פרטים
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => addOptionalField(groupIndex, itemIndex, 'link')}
                                                            disabled={item.optionalFields?.some(f => f.type === 'link')}
                                                        >
                                                            + קישור
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => addOptionalField(groupIndex, itemIndex, 'input')}
                                                            disabled={item.optionalFields?.some(f => f.type === 'input')}
                                                        >
                                                            + קלט
                                                        </button>
                                                    </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <button type="button" className="add-item-btn" onClick={() => addItem(groupIndex)}>
                                            <Plus size={16} />
                                            הוסף פריט
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}

                        {template.groups.length === 0 && (
                            <div className="empty-state">
                                <p>לא הוגדרו קבוצות עדיין. לחץ על &quot;הוסף קבוצה&quot; כדי להתחיל.</p>
                            </div>
                        )}
                    </div>

                    <div style={pageActionsContainer}>
                        <LoadingSubmitButton
                            type="submit"
                            style={confirmButton}
                            isLoading={saving}
                            text="שמור"
                            loadingText="שומר..."
                            disabled={saving}
                        />
                        <button
                            type="button"
                            style={cancelButton}
                            onClick={() => navigate('/checklists')}
                            disabled={saving}
                        >
                            ביטול
                        </button>
                        {isEditMode && user?.isSuperAdmin && (
                            <button
                                type="button"
                                onClick={handleExport}
                                className="export-link"
                                disabled={saving}
                            >
                                ייצוא
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </CommonLayout>
    );
};

export default AddEditChecklistTemplate;